import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { useAnalytics } from '@/hooks/useAnalytics'
import { storage } from '@/lib/storage'

const DEFAULT_TUTORIAL_KEY_PREFIX = 'dashboard_tutorial_seen'

export type CoachMarkStep = {
  testID: string
  titleKey: string
  messageKey: string
  messageKeyMobile?: string
}

type CoachMarkOptions = {
  storageKeyPrefix?: string
}

export type UseCoachMarkReturn = {
  showTutorial: boolean
  currentStep: number
  totalSteps: number
  goToNextStep: () => void
  goToPreviousStep: () => void
  completeTutorial: () => void
  skipTutorial: () => void
}

const STORAGE_DELAY_MS = 600

export function useCoachMark(
  steps: CoachMarkStep[],
  options?: CoachMarkOptions
): UseCoachMarkReturn {
  const { user } = useAuth()
  const { capture } = useAnalytics()
  const [showTutorial, setShowTutorial] = useState(false)
  const [currentStep, setCurrentStep] = useState(0)
  const hasChecked = useRef(false)

  const totalSteps = steps.length
  const keyPrefix = options?.storageKeyPrefix ?? DEFAULT_TUTORIAL_KEY_PREFIX

  const markSeen = useCallback(() => {
    if (!user?.id) return
    const key = `${keyPrefix}_${user.id}`
    storage.set(key, '1').catch(() => {})
  }, [user?.id, keyPrefix])

  useEffect(() => {
    if (!user?.id || hasChecked.current) return
    hasChecked.current = true

    const key = `${keyPrefix}_${user.id}`

    const timer = setTimeout(() => {
      storage
        .get(key)
        .then((seen) => {
          if (seen !== '1') {
            setShowTutorial(true)
            markSeen()
            capture('tutorial_started')
          }
        })
        .catch(() => {
          // On error, show tutorial anyway (better to show than permanently hide)
          setShowTutorial(true)
          markSeen()
          capture('tutorial_started')
        })
    }, STORAGE_DELAY_MS)

    return () => clearTimeout(timer)
  }, [user?.id, capture, keyPrefix, markSeen])

  const completeTutorial = useCallback(() => {
    capture('tutorial_completed', { total_steps: totalSteps })
    setShowTutorial(false)
    setCurrentStep(0)
    markSeen()
  }, [capture, totalSteps, markSeen])

  const skipTutorial = useCallback(() => {
    capture('tutorial_skipped', { step: currentStep + 1 })
    setShowTutorial(false)
    setCurrentStep(0)
    markSeen()
  }, [capture, currentStep, markSeen])

  const goToNextStep = useCallback(() => {
    if (currentStep >= totalSteps - 1) {
      completeTutorial()
      return
    }
    const nextStep = currentStep + 1
    capture('tutorial_step_viewed', { step: nextStep + 1 })
    setCurrentStep(nextStep)
  }, [currentStep, totalSteps, completeTutorial, capture])

  const goToPreviousStep = useCallback(() => {
    if (currentStep <= 0) return
    setCurrentStep((prev) => prev - 1)
  }, [currentStep])

  return {
    showTutorial,
    currentStep,
    totalSteps,
    goToNextStep,
    goToPreviousStep,
    completeTutorial,
    skipTutorial,
  }
}
